import React from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

const ArticleSkeleton = () => {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                width: "100%",
                backgroundColor: "#fff",
                alignItems: "center",
                padding: { xs: "20px", md: "40px" },
                marginTop: "120px",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    gap: "60px",
                    maxWidth: "1440px",
                    width: "100%",
                    justifyContent: "center",
                }}
            >
                {/* Main Content Skeleton */}
                <Box sx={{ flex: 1, maxWidth: "900px" }}>
                    {/* Title Skeleton */}
                    <Skeleton
                        variant="text"
                        sx={{
                            fontSize: "36px",
                            width: "70%",
                            mb: 2,
                            borderRadius: "8px",
                        }}
                        animation="wave"
                    />

                    {/* Meta Info Skeleton (Author, Date, Views) */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <Skeleton
                            variant="circular"
                            width={40}
                            height={40}
                            animation="wave"
                        />
                        <Box>
                            <Skeleton
                                variant="text"
                                width={120}
                                sx={{ fontSize: "14px" }}
                                animation="wave"
                            />
                            <Skeleton
                                variant="text"
                                width={180}
                                sx={{ fontSize: "12px" }}
                                animation="wave"
                            />
                        </Box>
                    </Box>

                    {/* Content Skeleton */}
                    <Box sx={{ mb: 4 }}>
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "100%" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "100%" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "95%" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "100%" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "88%" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "100%" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "75%" }}
                            animation="wave"
                        />
                    </Box>

                    {/* Image Placeholder Skeleton */}
                    <Skeleton
                        variant="rectangular"
                        sx={{
                            width: "100%",
                            height: "300px",
                            borderRadius: "12px",
                            mb: 4,
                        }}
                        animation="wave"
                    />

                    {/* More Content Skeleton */}
                    <Box sx={{ mb: 4 }}>
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "100%" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "92%" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "100%" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "16px", width: "85%" }}
                            animation="wave"
                        />
                    </Box>

                    {/* Tags Skeleton */}
                    <Box sx={{ display: "flex", gap: 1, mb: 4 }}>
                        <Skeleton
                            variant="rounded"
                            width={80}
                            height={28}
                            sx={{ borderRadius: "14px" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="rounded"
                            width={100}
                            height={28}
                            sx={{ borderRadius: "14px" }}
                            animation="wave"
                        />
                        <Skeleton
                            variant="rounded"
                            width={70}
                            height={28}
                            sx={{ borderRadius: "14px" }}
                            animation="wave"
                        />
                    </Box>

                    {/* Comments Section Skeleton */}
                    <Box sx={{ mt: 4 }}>
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "24px", width: "150px", mb: 2 }}
                            animation="wave"
                        />

                        {/* Comment Input Skeleton */}
                        <Skeleton
                            variant="rectangular"
                            sx={{
                                width: "100%",
                                height: "120px",
                                borderRadius: "8px",
                                mb: 3,
                            }}
                            animation="wave"
                        />

                        {/* Comment Items Skeleton */}
                        {[1, 2].map((item) => (
                            <Box
                                key={item}
                                sx={{
                                    display: "flex",
                                    gap: 2,
                                    mb: 3,
                                    p: 2,
                                    backgroundColor: "#F9FAFA",
                                    borderRadius: "8px",
                                }}
                            >
                                <Skeleton
                                    variant="circular"
                                    width={36}
                                    height={36}
                                    animation="wave"
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton
                                        variant="text"
                                        width={100}
                                        sx={{ fontSize: "14px" }}
                                        animation="wave"
                                    />
                                    <Skeleton
                                        variant="text"
                                        width="100%"
                                        sx={{ fontSize: "14px" }}
                                        animation="wave"
                                    />
                                    <Skeleton
                                        variant="text"
                                        width="80%"
                                        sx={{ fontSize: "14px" }}
                                        animation="wave"
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Sidebar Skeleton */}
                <Box
                    sx={{
                        width: { lg: "290px", xs: "100%" },
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                    }}
                >
                    {/* Related Articles Skeleton */}
                    <Box
                        sx={{
                            p: 2,
                            backgroundColor: "#F9FAFA",
                            borderRadius: "12px",
                        }}
                    >
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "18px", width: "140px", mb: 2 }}
                            animation="wave"
                        />
                        {[1, 2, 3].map((item) => (
                            <Box key={item} sx={{ mb: 2 }}>
                                <Skeleton
                                    variant="text"
                                    sx={{ fontSize: "14px", width: "100%" }}
                                    animation="wave"
                                />
                                <Skeleton
                                    variant="text"
                                    sx={{ fontSize: "12px", width: "60%" }}
                                    animation="wave"
                                />
                            </Box>
                        ))}
                    </Box>

                    {/* Category Skeleton */}
                    <Box
                        sx={{
                            p: 2,
                            backgroundColor: "#F9FAFA",
                            borderRadius: "12px",
                        }}
                    >
                        <Skeleton
                            variant="text"
                            sx={{ fontSize: "18px", width: "100px", mb: 2 }}
                            animation="wave"
                        />
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <Skeleton
                                variant="rounded"
                                width={70}
                                height={24}
                                sx={{ borderRadius: "12px" }}
                                animation="wave"
                            />
                            <Skeleton
                                variant="rounded"
                                width={90}
                                height={24}
                                sx={{ borderRadius: "12px" }}
                                animation="wave"
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ArticleSkeleton;
